'use client';

import { ElementType } from 'react';

import {
  Box,
  Button,
  Flex,
  Stack,
  Step,
  Stepper,
  StepSeparator,
  Text,
  useSteps,
  useTheme
} from '@chakra-ui/react';

import { BookingSteps } from 'types';

interface ProgressIndicatorProps {
  steps: {
    title: string;
    icon: ElementType;
    step: BookingSteps;
  }[];
  onChangeStep: (step: number) => void;
  currentStep: number;
  locationName?: string;
  isSingleLocation?: boolean;
  isLocationExist?: boolean;
}

const ProgressIndicator = ({
  steps,
  locationName = '',
  currentStep,
  onChangeStep,
  isSingleLocation = false,
  isLocationExist = true
}: ProgressIndicatorProps) => {
  const {
    colors: {
      palette: {
        components: { progress }
      }
    }
  } = useTheme();

  const { setActiveStep } = useSteps({
    index: currentStep,
    count: steps.length
  });

  const handleChange = (step: number) => () => {
    setActiveStep(step);
    onChangeStep(step);
  };

  return (
    <Flex
      align='center'
      w='100%'
      gap={{ base: '23px', md: '50px', lg: '71px' }}
      bg={progress.background}
      py='13.5px'
      px={['12px', '15px', '20px']}
      borderRadius={{ base: 0, sm: '4px' }}
      flexWrap={{ base: 'wrap', md: 'nowrap' }}
      width={{ base: 'calc(100% + 30px)', sm: '100%' }}
      mx={{ base: '-15px', sm: '0' }}
    >
      {isLocationExist && (
        <Stack
          alignItems={{ base: 'center', md: 'flex-start' }}
          flexDirection={{ base: 'row', md: 'column' }}
          justifyContent={{ base: 'space-between', md: 'center' }}
          w={{ base: '100%', md: '200px' }}
          gap='0'
        >
          <Text color={progress.locationNameColor} fontSize='18px'>
            {locationName}
          </Text>
          {!isSingleLocation && (
            <Button
              onClick={handleChange(BookingSteps.LOCATION)}
              variant='text'
              fontSize='11px'
              textTransform='uppercase'
              textDecoration='underline'
              color={progress.changeButtonColor}
              px={0}
              py='3px'
              minHeight='20px'
              height='auto'
            >
              Change
            </Button>
          )}
        </Stack>
      )}

      <Stepper
        size='sm'
        index={currentStep}
        gap='0'
        width='100%'
        mr={{ lg: '25px' }}
      >
        {steps.map(({ title, icon: Icon, step: bookingStep }, index) => {
          const isFirstItem = index === 0;
          const isPreviousItem = index < currentStep;
          const isActiveItem = index === currentStep;
          const isSeenItem = isPreviousItem || isActiveItem;

          const notActiveColor = isPreviousItem
            ? progress.stepIconActiveColor
            : progress.stepIconInactiveColor;
          const iconColor = isActiveItem
            ? progress.stepIconCurrentColor
            : notActiveColor;
          const separatorHeight = isSeenItem ? '8px' : '1px';
          const itemBorderRadius = isActiveItem ? '8px' : '0';

          return (
            <Box
              as={Step}
              key={title}
              onClick={handleChange(bookingStep || index)}
              style={{
                alignItems: 'flex-end',
                gap: 0,
                width: 'auto',
                padding: 0,
                background: 'none',
                pointerEvents: isPreviousItem ? 'auto' : 'none'
              }}
              sx={{
                '& div[role=separator][data-status=complete]': {
                  background: progress.stepSeparatorCompleteColor,
                  height: separatorHeight
                },
                '& div[role=separator][data-status=active], & div[role=separator][data-status=incomplete]':
                  {
                    background: progress.stepSeparatorIncompleteColor,
                    height: '1px',
                    pointerEvents: 'none'
                  }
              }}
            >
              <Stack alignItems='center' gap={0}>
                <Flex alignItems='center' gap={['3px', '5px', '8px']} mb='8px'>
                  <Icon width='14px' height='14px' fill={iconColor} />
                  <Text
                    color={
                      isSeenItem
                        ? progress.stepTextSeenColor
                        : progress.stepTextUnseenColor
                    }
                    textTransform='uppercase'
                    fontSize={{
                      base: '11px',
                      md: '12px',
                      lg: '14px'
                    }}
                    whiteSpace='nowrap'
                    lineHeight={1}
                  >
                    {title}
                  </Text>
                </Flex>

                <Flex height='8px' width='105%' alignItems='center'>
                  <Box
                    h='2px'
                    w='100%'
                    bg={iconColor}
                    height={separatorHeight}
                    borderRadius={itemBorderRadius}
                    borderLeftRadius={isFirstItem ? '8px' : itemBorderRadius}
                    zIndex={2}
                  />
                </Flex>
              </Stack>

              <Flex height='8px' w='100%' alignItems='center' border='none'>
                <StepSeparator style={{ margin: '0' }} />
              </Flex>
            </Box>
          );
        })}
      </Stepper>
    </Flex>
  );
};

export default ProgressIndicator;
